import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import {
  FileText, Search, Eye, Check, X, Trash2, AlertTriangle,
  ChevronLeft, ChevronRight, Clock, User, Calendar, Tag,
  Play, Image, MessageSquare
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/admin';

export default function AdminEventsPage() {
  const { token, hasPermission } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [search, setSearch] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewAction, setReviewAction] = useState({ status: '', reason: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchEvents();
    fetchStats();
  }, [pagination.page, statusFilter, search]);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: pagination.page, limit: 15, status: statusFilter, search }
      });
      setEvents(res.data.events || []);
      setPagination(res.data.pagination);
    } catch (err) {
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/events/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const viewEvent = async (eventId) => {
    try {
      const res = await axios.get(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedEvent(res.data);
    } catch (err) {
      toast.error('Failed to load event details');
    }
  };

  const handleReview = async () => {
    if (!selectedEvent || !reviewAction.status) return;

    try {
      await axios.put(`${API}/events/${selectedEvent.event.id}/review`, {
        status: reviewAction.status,
        reason: reviewAction.reason
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Event ${reviewAction.status} successfully`);
      setShowReviewModal(false);
      setReviewAction({ status: '', reason: '' });
      setSelectedEvent(null);
      fetchEvents();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to review event');
    }
  };

  const handleDelete = async () => {
    if (!selectedEvent) return;

    try {
      await axios.delete(`${API}/events/${selectedEvent.event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Event deleted successfully');
      setShowDeleteModal(false);
      setSelectedEvent(null);
      fetchEvents();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete event');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <span className="px-2.5 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded-full">Approved</span>;
      case 'rejected':
        return <span className="px-2.5 py-1 bg-red-500/20 text-red-400 text-xs font-medium rounded-full">Rejected</span>;
      default:
        return <span className="px-2.5 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full">Pending</span>;
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'video': return <Play className="w-4 h-4" />;
      case 'image': return <Image className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-events-page">
      <Toaster position="top-center" theme="dark" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>Events & Posts</h1>
          <p className="text-zinc-500 text-sm mt-1">Review and manage user-submitted content</p>
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <button
          onClick={() => { setStatusFilter('all'); setPagination(p => ({ ...p, page: 1 })); }}
          className={`bg-zinc-900/50 rounded-xl p-4 border transition-all text-left ${statusFilter === 'all' ? 'border-indigo-500' : 'border-zinc-800 hover:border-zinc-700'
            }`}
        >
          <p className="text-zinc-500 text-sm">Total Events</p>
          <p className="text-2xl font-bold text-white mt-1">{stats.total}</p>
        </button>
        <button
          onClick={() => { setStatusFilter('pending'); setPagination(p => ({ ...p, page: 1 })); }}
          className={`bg-zinc-900/50 rounded-xl p-4 border transition-all text-left ${statusFilter === 'pending' ? 'border-amber-500' : 'border-zinc-800 hover:border-zinc-700'
            }`}
        >
          <p className="text-zinc-500 text-sm">Pending Review</p>
          <p className="text-2xl font-bold text-amber-400 mt-1">{stats.pending}</p>
        </button>
        <button
          onClick={() => { setStatusFilter('approved'); setPagination(p => ({ ...p, page: 1 })); }}
          className={`bg-zinc-900/50 rounded-xl p-4 border transition-all text-left ${statusFilter === 'approved' ? 'border-emerald-500' : 'border-zinc-800 hover:border-zinc-700'
            }`}
        >
          <p className="text-zinc-500 text-sm">Approved</p>
          <p className="text-2xl font-bold text-emerald-400 mt-1">{stats.approved}</p>
        </button>
        <button
          onClick={() => { setStatusFilter('rejected'); setPagination(p => ({ ...p, page: 1 })); }}
          className={`bg-zinc-900/50 rounded-xl p-4 border transition-all text-left ${statusFilter === 'rejected' ? 'border-red-500' : 'border-zinc-800 hover:border-zinc-700'
            }`}
        >
          <p className="text-zinc-500 text-sm">Rejected</p>
          <p className="text-2xl font-bold text-red-400 mt-1">{stats.rejected}</p>
        </button>
      </div>

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPagination(p => ({ ...p, page: 1 }));
            }}
            placeholder="Search events by title, owner, or category..."
            className="w-full pl-10 pr-4 py-2.5 bg-zinc-800/50 border border-zinc-700 rounded-xl text-white placeholder:text-zinc-500 focus:outline-none focus:border-indigo-500"
          />
        </div>
      </div>

      {/* Events list */}
      <div className="bg-zinc-900/50 rounded-2xl border border-zinc-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Event</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Owner</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Category</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Status</th>
                <th className="text-left px-6 py-4 text-zinc-400 font-medium text-sm">Date</th>
                <th className="text-right px-6 py-4 text-zinc-400 font-medium text-sm">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={6} className="px-6 py-4">
                      <div className="h-16 bg-zinc-800 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : events.length > 0 ? (
                events.map((event) => (
                  <tr key={event.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center overflow-hidden">
                          {event.cover_photo ? (
                            <img
                              src={`https://naoview.app/resources/${event.cover_photo}`}
                              alt={event.event_data}
                              className="w-12 h-12 object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            getContentIcon(event.type)
                          )}
                        </div>
                        <div>
                          <p className="text-white font-medium truncate max-w-[200px]">
                            {event.event_data || 'Untitled Event'}
                          </p>
                          <p className="text-zinc-500 text-xs flex items-center gap-1">
                            <Tag className="w-3 h-3" /> {event.type || 'post'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                          {event.owner_avatar ? (
                            <img
                              src={`https://naoview.app/avatars/${event.owner_avatar}`}
                              alt={event.owner_name}
                              className="w-8 h-8 object-cover"
                            />
                          ) : (
                            <span className="text-white text-xs font-bold">
                              {event.owner_name?.charAt(0) || 'U'}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-zinc-300 text-sm">{event.owner_name || 'Unknown'}</p>
                          <p className="text-zinc-600 text-xs truncate max-w-[120px]">{event.event_owner}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-zinc-400 text-sm">{event.category || 'General'}</span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(event.review)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-zinc-500 text-sm">
                        {event.DateCreated ? new Date(event.DateCreated).toLocaleDateString() : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => viewEvent(event.id)}
                          className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg transition-all"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {hasPermission('streams.monitor') && (!event.review || event.review === 'pending') && (
                          <>
                            <button
                              onClick={() => {
                                viewEvent(event.id);
                                setTimeout(() => {
                                  setReviewAction({ status: 'approved', reason: '' });
                                  setShowReviewModal(true);
                                }, 300);
                              }}
                              className="p-2 text-emerald-400 hover:bg-emerald-500/20 rounded-lg transition-all"
                              title="Approve"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                viewEvent(event.id);
                                setTimeout(() => {
                                  setReviewAction({ status: 'rejected', reason: '' });
                                  setShowReviewModal(true);
                                }, 300);
                              }}
                              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-all"
                              title="Reject"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {hasPermission('streams.terminate') && (
                          <button
                            onClick={() => {
                              viewEvent(event.id);
                              setTimeout(() => setShowDeleteModal(true), 300);
                            }}
                            className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-zinc-500">
                    No events found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800">
            <p className="text-zinc-500 text-sm">
              Page {pagination.page} of {pagination.pages} ({pagination.total} events)
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page <= 1}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page >= pagination.pages}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Event Preview Modal */}
      {selectedEvent && !showReviewModal && !showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-zinc-800">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                    Event Preview
                  </h2>
                  <p className="text-zinc-500 text-sm mt-1">Review content before approval</p>
                </div>
                <button
                  onClick={() => setSelectedEvent(null)}
                  className="text-zinc-500 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Cover image */}
              {selectedEvent.event.cover_photo && (
                <div className="w-full h-48 rounded-xl overflow-hidden bg-zinc-800">
                  <img
                    src={`https://naoview.app/resources/${selectedEvent.event.cover_photo}`}
                    alt="Cover"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}

              {/* Event details */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white">{selectedEvent.event.event_data || 'Untitled'}</h3>
                  <div className="flex items-center gap-2 mt-2">
                    {getStatusBadge(selectedEvent.event.review)}
                    <span className="text-zinc-500 text-sm">• {selectedEvent.event.category || 'General'}</span>
                    <span className="text-zinc-500 text-sm">• {selectedEvent.event.type || 'Post'}</span>
                  </div>
                </div>

                {/* Owner info */}
                <div className="flex items-center gap-3 p-4 bg-zinc-800/30 rounded-xl">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center overflow-hidden">
                    {selectedEvent.event.owner_avatar ? (
                      <img
                        src={`https://naoview.app/avatars/${selectedEvent.event.owner_avatar}`}
                        alt={selectedEvent.event.owner_name}
                        className="w-12 h-12 object-cover"
                      />
                    ) : (
                      <User className="w-6 h-6 text-white" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">{selectedEvent.event.owner_name || 'Unknown User'}</p>
                    <p className="text-zinc-500 text-sm">{selectedEvent.event.event_owner}</p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-zinc-800/30 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-sm">Views</p>
                    <p className="text-xl font-bold text-white">{selectedEvent.stats?.views || 0}</p>
                  </div>
                  <div className="bg-zinc-800/30 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-sm">Likes</p>
                    <p className="text-xl font-bold text-white">{selectedEvent.stats?.likes || 0}</p>
                  </div>
                  <div className="bg-zinc-800/30 rounded-xl p-4 text-center">
                    <p className="text-zinc-500 text-sm">Price</p>
                    <p className="text-xl font-bold text-amber-400">{selectedEvent.event.event_price || 'Free'}</p>
                  </div>
                </div>

                {/* Dates */}
                <div className="flex items-center gap-4 text-zinc-500 text-sm">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Created: {selectedEvent.event.DateCreated ? new Date(selectedEvent.event.DateCreated).toLocaleString() : 'N/A'}
                  </span>
                </div>

                {/* Rejection reason if any */}
                {selectedEvent.event.reason_review && (
                  <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
                    <p className="text-red-400 text-sm font-medium">Rejection/Review Reason:</p>
                    <p className="text-zinc-400 text-sm mt-1">{selectedEvent.event.reason_review}</p>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              {hasPermission('streams.monitor') && (
                <div className="flex gap-3 pt-4 border-t border-zinc-800">
                  <button
                    onClick={() => {
                      setReviewAction({ status: 'approved', reason: '' });
                      setShowReviewModal(true);
                    }}
                    className="flex-1 py-3 bg-emerald-500/20 text-emerald-400 font-medium rounded-xl hover:bg-emerald-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    <Check className="w-4 h-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      setReviewAction({ status: 'rejected', reason: '' });
                      setShowReviewModal(true);
                    }}
                    className="flex-1 py-3 bg-red-500/20 text-red-400 font-medium rounded-xl hover:bg-red-500/30 transition-all flex items-center justify-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-md w-full border border-zinc-800">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                {reviewAction.status === 'approved' ? (
                  <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-400" />
                  </div>
                ) : (
                  <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                    <X className="w-5 h-5 text-red-400" />
                  </div>
                )}
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                    {reviewAction.status === 'approved' ? 'Approve Event' : 'Reject Event'}
                  </h3>
                  <p className="text-zinc-500 text-sm">{selectedEvent.event.event_data}</p>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">
                  {reviewAction.status === 'approved' ? 'Approval Note (optional)' : 'Rejection Reason'}
                </label>
                <textarea
                  value={reviewAction.reason}
                  onChange={(e) => setReviewAction(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={reviewAction.status === 'approved'
                    ? 'Add a note for the content owner...'
                    : 'Explain why this content is being rejected...'
                  }
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:outline-none focus:border-indigo-500 resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => {
                    setShowReviewModal(false);
                    setReviewAction({ status: '', reason: '' });
                  }}
                  className="flex-1 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReview}
                  className={`flex-1 py-3 font-bold rounded-xl transition-all flex items-center justify-center gap-2 ${reviewAction.status === 'approved'
                      ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                      : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                >
                  {reviewAction.status === 'approved' ? (
                    <>
                      <Check className="w-4 h-4" />
                      Approve
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4" />
                      Reject
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedEvent && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl max-w-md w-full border border-zinc-800">
            <div className="p-6 border-b border-zinc-800">
              <div className="flex items-center gap-3 text-red-400">
                <AlertTriangle className="w-6 h-6" />
                <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                  Delete Event
                </h3>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-zinc-400">
                Are you sure you want to permanently delete this event?
                <span className="block mt-2 text-white font-medium">
                  "{selectedEvent.event.event_data}"
                </span>
                <span className="block mt-1 text-zinc-500 text-sm">
                  by {selectedEvent.event.owner_name || selectedEvent.event.event_owner}
                </span>
              </p>
              <p className="text-red-400 text-sm">This action cannot be undone.</p>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 py-3 bg-zinc-800 text-white font-medium rounded-xl hover:bg-zinc-700 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}